import { Card, Button, Typography, Space, Row, Col, Divider, notification } from 'antd';
import {
    MessageOutlined,
    BankOutlined,
    FileTextOutlined,
    VideoCameraOutlined,
    UserOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import { PageTitle } from '../../components/PageTitle.tsx';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { cancelSubscription } from '../../services/api';
import { useSubscriptionEndDate } from '../../hooks/useSubscriptionEndDate';

const { Title, Paragraph, Text } = Typography;

export const CancelSilverPlus = () => {
    const navigate = useNavigate();
    const { formattedAccessEndDate, isLoading: isLoadingEndDate } = useSubscriptionEndDate();

    const cancelMutation = useMutation({
        mutationFn: cancelSubscription,
        onSuccess: () => {
            notification.success({
                message: 'Kündigung erfolgreich',
                description: formattedAccessEndDate ?
                    `Ihr silver+ Abonnement wurde erfolgreich gekündigt. Sie haben weiterhin Zugang bis zum ${formattedAccessEndDate}.` :
                    'Ihr silver+ Abonnement wurde erfolgreich gekündigt. Sie haben weiterhin Zugang bis zum Ende Ihrer aktuellen Abrechnungsperiode.'
            });
            navigate('/account');
        },
        onError: (error) => {
            console.error('Cancellation error:', error);
            notification.error({
                message: 'Fehler bei der Kündigung',
                description: 'Es ist ein Fehler beim Kündigen Ihres Abonnements aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.'
            });
        }
    });

    const handleKeepPlan = () => {
        navigate('/account');
    };

    const handleCompleteCancellation = () => {
        cancelMutation.mutate();
    };

    const featureCards = [
        {
            icon: <MessageOutlined style={{ fontSize: '20px', color: '#666' }} />,
            title: "Weniger Sichtbarkeit",
            description: "Ihre Nachrichten landen nicht mehr oben im Postfach der Arbeitgeber:",
            currentValue: "Oben im Postfach",
            newValue: "Normale Position",
            color: "#ff4d4f"
        },
        {
            icon: <BankOutlined style={{ fontSize: '20px', color: '#666' }} />,
            title: "Weniger Jobs",
            description: "Kein Zugang mehr zu exklusiven Teilzeit-Jobangeboten:",
            currentValue: "Alle Jobs verfügbar",
            newValue: "Nur öffentliche Jobs",
            color: "#ff4d4f"
        },
        {
            icon: <FileTextOutlined style={{ fontSize: '20px', color: '#666' }} />,
            title: "Weniger Vorteile",
            description: "Sie können diese Vorteile nicht mehr nutzen:",
            features: [
                { icon: <FileTextOutlined />, text: "Persönliche Anschreiben" },
                { icon: <VideoCameraOutlined />, text: "Interview-Training" },
                { icon: <UserOutlined />, text: "Digitaler Jobcoach" },
            ]
        }
    ];

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
            <PageTitle title="Was Sie erwartet" />

            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <Paragraph style={{ fontSize: '18px', color: '#666', maxWidth: '600px', margin: '0 auto' }}>
                    {isLoadingEndDate ?
                        "Wenn Sie heute kündigen, behalten Sie Zugang zu silver+ bis zum Ende Ihrer aktuellen Abrechnungsperiode. Danach:" :
                        `Wenn Sie heute kündigen, behalten Sie Zugang zu silver+ bis zum ${formattedAccessEndDate}. Danach:`
                    }
                </Paragraph>
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: '40px' }}>
                {featureCards.map((card, index) => (
                    <Col xs={24} sm={12} lg={8} key={index}>
                        <Card
                            style={{
                                height: '100%',
                                borderRadius: '12px',
                                border: '1px solid #f0f0f0',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                            bodyStyle={{ padding: '24px' }}
                        >
                            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                                {card.icon}
                            </div>

                            <Title level={5} style={{ textAlign: 'center', marginBottom: '12px' }}>
                                {card.title}
                            </Title>

                            <Paragraph style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '16px' }}>
                                {card.description}
                            </Paragraph>

                            {card.currentValue && card.newValue ? (
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <Text style={{ color: '#999' }}>{card.currentValue}</Text>
                                        <ArrowRightOutlined style={{ color: '#ccc' }} />
                                        <Text style={{ color: card.color, fontWeight: 'bold' }}>{card.newValue}</Text>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    {card.features?.map((feature, featureIndex) => (
                                        <div key={featureIndex} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <span style={{ color: '#666' }}>{feature.icon}</span>
                                            <Text style={{ fontSize: '14px', color: '#666' }}>{feature.text}</Text>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </Col>
                ))}
            </Row>

            <Divider />

            <div style={{ textAlign: 'center' }}>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Button
                        size="large"
                        style={{ width: '300px' }}
                        onClick={handleKeepPlan}
                    >
                        Mein Abonnement behalten
                    </Button>

                    <Button
                        type="primary"
                        danger
                        size="large"
                        style={{ width: '300px' }}
                        loading={cancelMutation.isPending}
                        onClick={handleCompleteCancellation}
                    >
                        Kündigung abschließen
                    </Button>
                </Space>
            </div>
        </div>
    );
};
